import { useState, type ReactNode } from 'react';
import type { PersonalFolder, PlanChangeLine } from '@zerogap/shared';
import { post } from '../api';
import { locale, tr } from '../i18n';
import { FOLDER_LABEL, listJoin, spanText, weekday } from '../pages/plan-form';
import { useTenant } from '../session';
import { Button, bytes, CopyBox, friendlyError, vdisplay, when } from '../ui';

/** A personal folder by its translated name, anything else as a path kept left-to-right. */
function Folder({ value }: { value: string }) {
  const label = FOLDER_LABEL[value as PersonalFolder];
  return label ? <>{tr(label)}</> : <bdi className="mono">{value}</bdi>;
}

/** Splits a translated sentence at `{x}` so a path or name can be dropped in as an element. */
function Sentence({ text, x }: { text: string; x: ReactNode }) {
  const [before, after] = text.split('{x}');
  return (
    <>
      {before}
      {x}
      {after}
    </>
  );
}

/** One change to a plan, in a sentence. Paths stay as the computer writes them, left to right. */
function ChangeLine({ l }: { l: PlanChangeLine }) {
  const path = <bdi className="mono">{l.value}</bdi>;
  switch (l.type) {
    case 'add_folder':
      return <Sentence text={tr('Back up {x}', { x: '{x}' })} x={path} />;
    case 'remove_folder':
      return <Sentence text={tr('Stop backing up {x}', { x: '{x}' })} x={path} />;
    case 'add_personal':
      return <Sentence text={tr('Back up everybody’s {x}', { x: '{x}' })} x={<Folder value={l.value} />} />;
    case 'remove_personal':
      return <Sentence text={tr('Stop backing up everybody’s {x}', { x: '{x}' })} x={<Folder value={l.value} />} />;
    case 'skip_file':
      return <Sentence text={tr('Leave out {x}', { x: '{x}' })} x={path} />;
    case 'unskip_file':
      return <Sentence text={tr('Stop leaving out {x}', { x: '{x}' })} x={path} />;
    case 'skip_pattern':
      return <Sentence text={tr('Leave out files matching {x}', { x: '{x}' })} x={path} />;
    case 'unskip_pattern':
      return <Sentence text={tr('Stop leaving out files matching {x}', { x: '{x}' })} x={path} />;
    case 'skip_type':
      return <Sentence text={tr('Leave out {x} files', { x: '{x}' })} x={<bdi className="mono">.{l.value}</bdi>} />;
    case 'unskip_type':
      return <Sentence text={tr('Stop leaving out {x} files', { x: '{x}' })} x={<bdi className="mono">.{l.value}</bdi>} />;
    case 'schedule':
      return (
        <>
          {l.value === 'several-daily'
            ? tr('Several times a day (every 3 hours, 08:00–21:00)')
            : l.value === 'weekly'
              ? tr('Every {day} at {time}', { day: weekday(l.day ?? 1, 'long'), time: l.time ?? '' })
              : tr('Every day at {time}', { time: l.time ?? '' })}
        </>
      );
    case 'keep_history':
      return <>{tr('Keep about {span} of history', { span: spanText(l.days) })}</>;
    case 'rename':
      return <>{tr('Rename it to “{name}”', { name: l.value })}</>;
  }
}

function PlanChanges({ s }: { s: Record<string, any> }) {
  const n = (x: number) => Number(x ?? 0).toLocaleString(locale());
  return (
    <>
      {(s.lines ?? []).length > 0 && (
        <ul className="assist-paths">
          {(s.lines as PlanChangeLine[]).map((l, i) => (
            <li key={i}><ChangeLine l={l} /></li>
          ))}
        </ul>
      )}
      {s.stopsSaving && s.stopsSaving.files > 0 && (
        <p className="small" style={{ margin: 0 }}>
          {s.stopsSaving.files === 1
            ? tr('The newest backup has one such file ({size}); it will no longer be backed up.', { size: bytes(s.stopsSaving.bytes) })
            : tr('The newest backup has {files} such files ({size}); they will no longer be backed up.', { files: n(s.stopsSaving.files), size: bytes(s.stopsSaving.bytes) })}
        </p>
      )}
      {s.reduces && <p className="small muted" style={{ margin: 0 }}>{tr('From the next backup, what is left out is no longer saved. Backups already taken still have it.')}</p>}
    </>
  );
}

/**
 * The card a person reads before an action happens.
 *
 * Everything on it comes from the server's `summary`: names it resolved from ids, counts and sizes it
 * measured against the catalog. None of it is the assistant's own description of its own proposal, which
 * is the point — a wrong or manipulated sentence in the chat cannot make this card say something else,
 * and nothing happens until the button below it is pressed.
 */

export interface Proposal {
  id: string;
  tool: string;
  action: string;
  summary: Record<string, any>;
  expiresAt: string;
  state?: string;
}

/** The consequence in one plain sentence, plus the details that justify it. */
function Body({ s }: { s: Record<string, any> }) {
  const n = (x: number) => Number(x ?? 0).toLocaleString(locale());
  switch (s.kind) {
    case 'back_up_now':
      return <p>{tr('Back up {computer} now, without waiting for its usual time.', { computer: s.computer })}</p>;

    case 'cancel_backup':
      return (
        <p>
          {tr('Stop the backup running on {computer}.', { computer: s.computer })}{' '}
          <span className="muted">{tr('Everything sent so far is kept and reused next time.')}</span>
        </p>
      );

    case 'pause_backup_plan':
      return (
        <p>
          {s.computers === 1
            ? tr('Pause the plan “{plan}”, so its one computer stops backing up until it is switched back on.', { plan: s.plan })
            : tr('Pause the plan “{plan}”, so {n} computers stop backing up until it is switched back on.', { plan: s.plan, n: n(s.computers) })}{' '}
          <span className="muted">{tr('Backups already saved are not affected.')}</span>
        </p>
      );

    case 'resume_backup_plan':
      return (
        <p>
          {s.computers === 1
            ? tr('Switch the plan “{plan}” back on, so its computer returns to its schedule.', { plan: s.plan })
            : tr('Switch the plan “{plan}” back on, so its {n} computers return to their schedule.', { plan: s.plan, n: n(s.computers) })}
        </p>
      );

    case 'settle_alert':
      return (
        <p>
          {s.as === 'resolve' ? tr('Mark the alert “{title}” as dealt with.', { title: s.title }) : tr('Mark the alert “{title}” as seen.', { title: s.title })}
        </p>
      );

    case 'cancel_recovery':
      return (
        <p>
          {tr('Stop this recovery.')} <span className="muted">{tr('{done} of {total} files have arrived; those stay where they are.', { done: n(s.doneFiles), total: n(s.totalFiles) })}</span>
        </p>
      );

    case 'retry_recovery':
      return (
        <p>
          {s.failedFiles === 1
            ? tr('Send the one file that did not arrive to {computer} again.', { computer: s.computer })
            : tr('Send the {n} files that did not arrive to {computer} again.', { n: n(s.failedFiles), computer: s.computer })}{' '}
          <span className="muted">{tr('Files that already arrived are left as they are.')}</span>
        </p>
      );

    case 'keep_backup':
      return (
        <div className="stack" style={{ gap: 10 }}>
          <p style={{ margin: 0 }}>
            {s.backupTakenAt
              ? tr('Keep the backup of {computer} saved {time}, so it is never deleted on schedule.', { computer: s.computer, time: when(s.backupTakenAt) })
              : tr('Keep this backup of {computer}, so it is never deleted on schedule.', { computer: s.computer })}
          </p>
          {s.reason && <p className="small muted" style={{ margin: 0 }}>{tr('Saved with the note: “{reason}”', { reason: s.reason })}</p>}
          <p className="small" style={{ margin: 0 }}>{tr('It stays until somebody chooses “Stop keeping” on that computer’s page. Kept backups count towards your storage.')}</p>
        </div>
      );

    case 'pause_cleanup':
      return (
        <div className="stack" style={{ gap: 10 }}>
          <p style={{ margin: 0 }}>
            {tr('Stop deleting old backups for {n} days, until {date}, for every computer.', { n: n(s.days), date: when(s.until) })}{' '}
            <span className="muted">{tr('New backups carry on as usual.')}</span>
          </p>
          {s.reason && <p className="small muted" style={{ margin: 0 }}>{tr('Saved with the note: “{reason}”', { reason: s.reason })}</p>}
          <p className="small" style={{ margin: 0 }}>{tr('Old backups take up storage while they are kept. You can go back to normal on the Overview page at any time.')}</p>
        </div>
      );

    case 'change_plan':
      return (
        <div className="stack" style={{ gap: 10 }}>
          <p style={{ margin: 0 }}>
            {(s.computers ?? []).length === 0
              ? tr('Change the plan “{plan}”. No computer uses it yet.', { plan: s.plan })
              : tr('Change the plan “{plan}”, for {computers}.', { plan: s.plan, computers: listJoin(s.computers) })}
          </p>
          <PlanChanges s={s} />
        </div>
      );

    case 'copy_plan_for_computers':
      return (
        <div className="stack" style={{ gap: 10 }}>
          <p style={{ margin: 0 }}>
            {(s.lines ?? []).length
              ? tr('Make a new plan “{name}”, a copy of “{from}” with these changes:', { name: s.name, from: s.from })
              : tr('Make a new plan “{name}”, a copy of “{from}”.', { name: s.name, from: s.from })}
          </p>
          <PlanChanges s={s} />
          {(s.moving ?? []).length > 0 && <p style={{ margin: 0 }}>{tr('Move {computers} onto it.', { computers: listJoin(s.moving) })}</p>}
          {s.staying > 0 && (
            <p className="small muted" style={{ margin: 0 }}>
              {s.staying === 1
                ? tr('The other computer stays on “{from}”, unchanged.', { from: s.from })
                : tr('The other {n} computers stay on “{from}”, unchanged.', { n: n(s.staying), from: s.from })}
            </p>
          )}
        </div>
      );

    case 'move_computers_to_plan':
      return (
        <div className="stack" style={{ gap: 10 }}>
          {(s.computers ?? []).map((c: { name: string; from: string | null; gains: string[]; loses: string[] }) => (
            <div key={c.name} className="stack" style={{ gap: 4 }}>
              <p style={{ margin: 0 }}>
                {c.from ? tr('Move {computer} from “{from}” to “{plan}”.', { computer: c.name, from: c.from, plan: s.plan }) : tr('Put {computer} on “{plan}”.', { computer: c.name, plan: s.plan })}
              </p>
              {c.gains.length > 0 && (
                <p className="small" style={{ margin: 0 }}>
                  {tr('It will also back up:')} {c.gains.map((g, i) => <span key={g}>{i > 0 && ', '}<Folder value={g} /></span>)}
                </p>
              )}
              {c.loses.length > 0 && (
                <p className="small" style={{ margin: 0, color: 'var(--warn)' }}>
                  {tr('It will no longer back up:')} {c.loses.map((g, i) => <span key={g}>{i > 0 && ', '}<Folder value={g} /></span>)}
                </p>
              )}
            </div>
          ))}
          <p className="small muted" style={{ margin: 0 }}>{tr('It applies from each computer’s next backup.')}</p>
        </div>
      );

    case 'recover_files':
      return (
        <div className="stack" style={{ gap: 10 }}>
          {/* One file is the commonest recovery of all, and "1 files" reads as a machine talking. */}
          <p style={{ margin: 0 }}>
            {s.deliver === 'download'
              ? s.files === 1
                ? tr('Prepare one file ({size}) from {computer} as a download.', { size: bytes(s.bytes), computer: s.computer })
                : tr('Prepare {files} files ({size}) from {computer} as a single download.', { files: n(s.files), size: bytes(s.bytes), computer: s.computer })
              : s.files === 1
                ? tr('Put one file ({size}) back on {computer}, in a new folder named after today.', { size: bytes(s.bytes), computer: s.computer })
                : tr('Put {files} files ({size}) back on {computer}, in a new folder named after today.', { files: n(s.files), size: bytes(s.bytes), computer: s.computer })}
          </p>
          {s.backupTakenAt && <p className="small muted" style={{ margin: 0 }}>{tr('From the backup saved {time}.', { time: when(s.backupTakenAt) })}</p>}
          <ul className="assist-paths">
            {(s.paths ?? []).map((p: string) => (
              <li key={p}><bdi className="mono">{vdisplay(p, s.pathStyle)}</bdi></li>
            ))}
            {s.pathCount > (s.paths ?? []).length && <li className="muted">{tr('and {n} more', { n: n(s.pathCount - s.paths.length) })}</li>}
          </ul>
          <p className="small" style={{ margin: 0 }}>
            {s.deliver === 'download'
              ? tr('Nothing on the computer changes. You get a file to download when it is ready.')
              : tr('Nothing that is already on the computer is replaced. If a file with the same name is there, the recovered one is saved beside it.')}
          </p>
        </div>
      );

    case 'create_shared_link':
      return (
        <div className="stack" style={{ gap: 10 }}>
          <p style={{ margin: 0 }}>
            {s.files === 1
              ? tr('Make a link to one file ({size}) from {computer}, called “{name}”.', { size: bytes(s.bytes), computer: s.computer, name: s.name })
              : tr('Make a link to {files} files ({size}) from {computer}, called “{name}”.', { files: n(s.files), size: bytes(s.bytes), computer: s.computer, name: s.name })}
          </p>
          <ul className="assist-paths">
            {(s.paths ?? []).map((p: string) => (
              <li key={p}><bdi className="mono">{vdisplay(p, s.pathStyle)}</bdi></li>
            ))}
            {s.pathCount > (s.paths ?? []).length && <li className="muted">{tr('and {n} more', { n: n(s.pathCount - s.paths.length) })}</li>}
          </ul>
          <p className="small" style={{ margin: 0 }}>
            <strong>
              {s.password
                ? tr('Anyone who has the link and its password can see and download these files until {date}.', { date: when(s.endsAt) })
                : tr('Anyone who has the link can see and download these files until {date}. No password.', { date: when(s.endsAt) })}
            </strong>
          </p>
          <p className="small muted" style={{ margin: 0 }}>
            {s.look === 'gallery' ? tr('It shows the files as a photo gallery.') : tr('It shows the files as a list.')}{' '}
            {tr('Every visit and download is recorded, and you can stop the link at any time.')}
          </p>
        </div>
      );

    case 'change_shared_link':
      return (
        <div className="stack" style={{ gap: 6 }}>
          <p style={{ margin: 0 }}>{tr('Change the shared link “{name}”:', { name: s.name })}</p>
          <ul className="assist-paths">
            {s.rename && <li>{tr('Rename it to “{name}”', { name: s.rename })}</li>}
            {s.look && <li>{s.look === 'gallery' ? tr('Show it as a photo gallery') : tr('Show it as a list')}</li>}
            {s.endsAt && <li>{tr('Make it end on {date}', { date: when(s.endsAt) })}</li>}
          </ul>
        </div>
      );

    case 'stop_shared_link':
      return (
        <p>
          {tr('Stop the shared link “{name}” now. It stops working at once, also for anyone who has it open.', { name: s.name })}{' '}
          <span className="muted">{tr('The files themselves are not touched.')}</span>
        </p>
      );

    default:
      return <p>{tr('Carry out this action.')}</p>;
  }
}

/** A new link, shown once to the person who confirmed it: the assistant never sees it. */
function LinkReveal({ link, password }: { link: string; password: string | null }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <CopyBox text={link} label={tr('Link')} />
      <div className="row" style={{ gap: 8 }}>
        <a className="btn sm" href={`mailto:?body=${encodeURIComponent(link)}`}>{tr('Send by email')}</a>
        <a className="btn sm" href={`https://wa.me/?text=${encodeURIComponent(link)}`} target="_blank" rel="noopener noreferrer">{tr('Send on WhatsApp')}</a>
      </div>
      {password && <CopyBox text={password} label={tr('Password')} />}
      {password && <p className="small" style={{ margin: 0 }}>{tr('Send the password separately from the link. It is not shown again.')}</p>}
    </div>
  );
}

export function ProposalCard({ proposal, onDecided }: { proposal: Proposal; onDecided: (state: string) => void }) {
  const t = useTenant();
  const [busy, setBusy] = useState<'confirm' | 'decline' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState(proposal.state ?? 'pending');
  /** Why the action did not work, in the portal's own words when it knows the reason. */
  const [why, setWhy] = useState<string | null>(null);
  /** A secret the action produced for this person's eyes only (a new shared link). Kept in memory, never stored. */
  const [reveal, setReveal] = useState<{ link: string; password: string | null } | null>(null);
  const expired = new Date(proposal.expiresAt) < new Date();

  const decide = async (decision: 'confirm' | 'decline') => {
    setBusy(decision);
    setError(null);
    try {
      const res = await post(`/api/t/${t.tenantId}/assistant/proposals/${proposal.id}`, { decision });
      // The route's own refusal comes back as the outcome: say it, rather than only "this did not work".
      if (res.state === 'failed') {
        const err = (res.outcome as { error?: { code?: string; message?: string } } | undefined)?.error;
        setWhy(err ? friendlyError(err) : null);
      }
      if (res.reveal?.link) setReveal({ link: res.reveal.link, password: res.reveal.password ?? null });
      setState(res.state);
      onDecided(res.state);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(null);
    }
  };

  if (state !== 'pending')
    return (
      <div className="assist-proposal settled">
        <Body s={proposal.summary} />
        <p className="small muted" style={{ margin: 0 }}>
          {state === 'declined' ? tr('You said no to this.') : state === 'failed' ? tr('This did not work.') : tr('Done.')}
        </p>
        {state === 'failed' && why && <p className="small" style={{ color: 'var(--bad)', margin: 0 }}>{why}</p>}
        {reveal && <LinkReveal link={reveal.link} password={reveal.password} />}
      </div>
    );

  return (
    <div className="assist-proposal" role="group" aria-label={tr('Confirm before this happens')}>
      <div className="assist-proposal-head">{tr('Confirm before this happens')}</div>
      <Body s={proposal.summary} />
      {error && <p className="small" style={{ color: 'var(--bad)', margin: 0 }}>{error}</p>}
      {expired ? (
        <p className="small muted" style={{ margin: 0 }}>{tr('This offer is out of date. Ask again and the details will be checked afresh.')}</p>
      ) : (
        <div className="row" style={{ gap: 8 }}>
          <Button variant="primary" size="sm" busy={busy === 'confirm'} disabled={!!busy} onClick={() => decide('confirm')}>{tr(proposal.action)}</Button>
          <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => decide('decline')}>{tr('No, thanks')}</Button>
        </div>
      )}
    </div>
  );
}
